import Image from '@/globalComponents/image';
import { Card } from '../card';
import DeviceBadge from '../deviceBadge';
import styles from './style.module.scss';
import React from 'react';
import { baseRouteLabels } from '@/labels/baseRoute';
import { SelectDeviceProps } from './type';
import ImageWithFallback from '@/globalComponents/image';
import LocationIcon from '../../../public/images/icons/location.svg';
import DeviceCard from '@/components/Device/DeviceCard';
import Header from '@/shared/Header';
import Button from '@/shared/Button';
import Div from '@/shared/Div';
import { getAddress } from '@/utils/address';
import Paragraph from '@/shared/Paragraph';
import classNames from 'classnames';
import NoDeviceCard from '@/components/Device/NoDeviceCard';
import is from '@/utils/is';
import { useProductContext } from '@/context/productContext';

const renderDeviceName = (title: string) => {
  return title.length > 100 ? `${title.slice(0, 100)}...` : title;
};

const ProductBanner = (props: SelectDeviceProps) => {
  const {
    deviceDetails,
    onClick,
    showSelection,
    isSelected = false,
    onSelected,
    dataTestId,
    isInsideModal,
    badgeClasses,
    productImageClasses,
    productLocationClasses,
    productTitleClasses,
    cardClasses,
    isMultipleDevices,
    newDeviceCard,
    isOpenServiceRequest,
    savingPromotionData = {},
    showBookServiceCta,
  } = props;

  const {
    img: image,
    name: title,
    address,
    expiryStatus = {
      isAboutToExpire: '',
      status: '',
    },
    planType,
    id,
    currentPlan,
    deviceId,
    isRental,
    deviceFamilyName,
    upcomingAMCPlan,
  } = deviceDetails;

  const { isAboutToExpire, status } = expiryStatus;
  const getPlanStatus = () => {
    if (status === 'IC' && isAboutToExpire) {
      return 'AMC Expiring';
    }
    if (status == 'IC' && !isAboutToExpire) {
      return planType;
    }
    if (status === 'IW' && isAboutToExpire) {
      return 'Warranty Expiring';
    }
    if (status === 'IW' && !isAboutToExpire) {
      return 'In Warranty';
    }
  };

  const background = 'linear-gradient(180deg, #434343 0%, #CFCFCF 100%)';
  const { state: product } = useProductContext();

  const renderDeviceCard = () => {
    if (newDeviceCard && is.empty.array(product.list)) {
      return <NoDeviceCard className="mt-2" />;
    }

    if (!title || title.length === 0) {
      return (
        <div data-testid="device-error-message" className="flex flex-col text-center">
          <p>Something went wrong. Unable to load device details</p>
        </div>
      );
    }

    /* Render Device Card only on Page instead of Device Selection Modal */
    if (newDeviceCard) {
      return (
        <DeviceCard
          name={renderDeviceName(title)}
          address={address}
          planType={planType}
          className="mt-2"
          expiryStatus={expiryStatus}
          currentPlan={currentPlan}
          id={id}
          upcomingAMCPlan={upcomingAMCPlan}
          deviceFamilyName={deviceFamilyName}
          deviceId={deviceId}
          isRental={isRental}
          image={image}
          isOpenServiceRequest={isOpenServiceRequest}
          savingPromotionData={savingPromotionData}
          showBookServiceCta={showBookServiceCta}
        />
      );
    }

    return (
      <>
        {!isInsideModal ? (
          <Card
            data-testid="device-card"
            classNames={`flex flex-row justify-between items-center relative ${cardClasses} ${styles.card} w-full`}
          >
            <div className="flex flex-row items-center gap-s4 md:gap-5 z-[1] w-full">
              <div
                className={`h-[4.6875rem] w-16 lg:h-[110px] lg:w-[95px] self-start  ${productImageClasses}`}
              >
                <Image
                  key={image}
                  type="dynamic"
                  src={image}
                  height={102}
                  width={117}
                  alt={title}
                  className={`h-full w-full lg:h-[110px] lg:w-[95px] md:self-center ${productImageClasses}`}
                  data-testid="device-image"
                  fallbackSrc="/images/productPlaceholder.svg"
                />
              </div>
              <div className="flex flex-col items-start h-full self-start gap-[10px] md:gap-s5 w-full">
                <div className="flex flex-col gap-[10px] md:flex-row md:justify-between w-full">
                  <p data-testid="device-name" className={`${productTitleClasses} ${styles.title}`}>
                    {renderDeviceName(title)}
                  </p>
                  <DeviceBadge tierNameClasses={badgeClasses} status={getPlanStatus()} />
                </div>
                {address && address.city && address.postal_code && (
                  <div data-testid="device-location" className="flex items-center gap-s3">
                    <ImageWithFallback
                      src={LocationIcon}
                      alt="locationIcon"
                      className="md:w-4 md:h-[1.3125rem] w-[0.875rem] h-[1.125rem]"
                    />
                    <p
                      className={`${productLocationClasses}`}
                    >{`${address.city} - ${address.postal_code}`}</p>
                  </div>
                )}
              </div>
            </div>
          </Card>
        ) : (
          <Div
            className="p-px rounded-cm bg-neutral-50 cursor-pointer"
            style={
              isSelected
                ? {
                    background,
                  }
                : {}
            }
          >
            <Div className="bg-white rounded-cm">
              <Div
                className={classNames(
                  'flex flex-col p-4 gap-s3 bg-white rounded-cm  md:p-5 md:gap-s3  md:flex-row lg:p-5 lg:gap-s3 lg:flex-row',
                  {
                    ' border-gray-400 shadow-medium': isSelected,
                  },
                )}
                style={
                  isSelected
                    ? {
                        background:
                          'linear-gradient(127.01deg, #E8F2FF 10.96%, rgba(232, 242, 255, 0)',
                      }
                    : {}
                }
              >
                <ImageWithFallback
                  key={image}
                  type="dynamic"
                  src={image}
                  height={68}
                  width={68}
                  alt={title}
                  data-testid="device-image"
                  fallbackSrc="/images/productPlaceholder.svg"
                  className="md:h-20 md:w-20 lg:h-20 lg:w-20"
                />
                <Div className="flex flex-col gap-s3 md:justify-center">
                  <Paragraph className="text-h-4">{renderDeviceName(title)}</Paragraph>
                  <Paragraph>{getAddress(address)}</Paragraph>
                  {isRental && (
                    <Div className="bg-orange-100 py-1 px-2 text-body-xs w-fit text-orange-600 rounded">
                      RENTED DEVICE
                    </Div>
                  )}
                </Div>
              </Div>
            </Div>
          </Div>
        )}
      </>
    );
  };

  return (
    <div
      data-testid={dataTestId}
      onClick={() => showSelection && onSelected && onSelected(deviceDetails)}
    >
      {!showSelection && (
        <div className="flex flex-row items-center justify-between mb-2">
          <Header data-testid="card-label" className=" !text-h-3 mt-s2 mb-s2">
            Your device details
          </Header>
        </div>
      )}
      {renderDeviceCard()}
    </div>
  );
};

export default ProductBanner;
